import React, { useState } from 'react';
import { Container, Text, Button } from 'components';
import ConfirmDialog from '../ConfirmDialog';
import { FiDownload, FiRefreshCw, FiEdit, FiCopy, FiTrash2, FiArrowUp, FiArrowDown, FiCheck, FiX } from 'react-icons/fi';
import { EdgeSubscriptionData } from './types';
import { hooks, mainOperations } from 'state';
import * as XLSX from 'xlsx';

interface EdgeSubscriptionsListProps {
  subscriptions: EdgeSubscriptionData[];
  onToggleActive?: (id: string) => void;
  onDelete?: (id: string) => void;
  isFetching?: boolean;
}

const EdgeSubscriptionsList: React.FC<EdgeSubscriptionsListProps> = ({
  subscriptions,
  onToggleActive,
  onDelete,
  isFetching = false,
}) => {
  const { useAppDispatch } = hooks;
  const { setModalOpen, setSelectedModalContent } = mainOperations;
  const dispatch = useAppDispatch();
  const STORAGE_SELECTED_KEY = 'edge_subscription_selected';
  
  const [filterEmail, setFilterEmail] = useState('');
  const [filterBrandName, setFilterBrandName] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterState, setFilterState] = useState('');
  const [edgeTypeFilter, setEdgeTypeFilter] = useState<'brand' | 'opportunity' | 'inventory' | 'all'>('all');
  const [sortField, setSortField] = useState<string | null>('createdModified');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const persistSelectedSubscription = (subscriptionId: string) => {
    if (typeof window === 'undefined') return;
    const selected = subscriptions.find(item => item.id === subscriptionId);
    if (!selected) return;
    try {
      const serializable = {
        ...selected,
        startDate: selected.startDate?.toISOString?.() || selected.startDate,
        nextDelivery: selected.nextDelivery?.toISOString?.() || selected.nextDelivery,
        createdOn: selected.createdOn?.toISOString?.() || selected.createdOn,
        modifiedOn: selected.modifiedOn?.toISOString?.() || selected.modifiedOn,
      };
      window.localStorage.setItem(STORAGE_SELECTED_KEY, JSON.stringify(serializable));
    } catch {
      // noop
    }
  };

  const handleEdit = (subscriptionId: string) => {
    persistSelectedSubscription(subscriptionId);
    dispatch(setModalOpen(true));
    dispatch(setSelectedModalContent(`edgeSubscriptionEdit-${subscriptionId}`));
  };

  const handleDuplicate = (subscriptionId: string) => {
    dispatch(setModalOpen(true));
    dispatch(setSelectedModalContent(`edgeSubscriptionDuplicate-${subscriptionId}`));
  };

  const handleDelete = (subscriptionId: string) => {
    setDeleteTargetId(subscriptionId);
  };

  const stateOptions = [
    { value: '', label: 'State' },
    { value: 'CA', label: 'California' },
    { value: 'TX', label: 'Texas' },
    { value: 'NY', label: 'New York' },
    { value: 'FL', label: 'Florida' },
  ];

  const companyOptions = [
    { value: '', label: 'Company' },
    { value: 'company1', label: 'Company 1' },
    { value: 'company2', label: 'Company 2' },
  ];

  const edgeTypeOptions = [
    { value: 'all', label: 'Edge Type' },
    { value: 'brand', label: 'Brand' },
    { value: 'opportunity', label: 'Opportunity' },
    { value: 'inventory', label: 'Inventory' },
  ];

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(date);
  };

  const formatDateShort = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const capitalizeFirst = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

  const getInitials = (email: string) => {
    return email.charAt(0).toUpperCase();
  };

  const getRecipientsList = (subscription: EdgeSubscriptionData) => {
    const raw = subscription.recipient || subscription.recipientEmail || '';
    return raw
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (edgeTypeFilter !== 'all' && sub.edgeType !== edgeTypeFilter) return false;
    if (filterEmail && !sub.recipientEmail.toLowerCase().includes(filterEmail.toLowerCase())) return false;
    if (filterBrandName && !sub.brandName.toLowerCase().includes(filterBrandName.toLowerCase())) return false;
    if (filterState && sub.state !== filterState && sub.territory !== filterState) return false;
    return true;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSubscriptions = [...filteredSubscriptions].sort((a, b) => {
    if (!sortField) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'subscriptionName':
        aValue = (a.subscriptionName || '').toLowerCase();
        bValue = (b.subscriptionName || '').toLowerCase();
        break;
      case 'edgeType':
        aValue = a.edgeType;
        bValue = b.edgeType;
        break;
      case 'periodicity':
        aValue = a.periodicity;
        bValue = b.periodicity;
        break;
      case 'brandName':
        aValue = a.brandName;
        bValue = b.brandName;
        break;
      case 'state':
        aValue = (a.state || a.territory || '').toLowerCase();
        bValue = (b.state || b.territory || '').toLowerCase();
        break;
      case 'nextDelivery':
        aValue = new Date(a.nextDelivery).getTime();
        bValue = new Date(b.nextDelivery).getTime();
        break;
      case 'recipientEmail':
        aValue = a.recipientEmail.toLowerCase();
        bValue = b.recipientEmail.toLowerCase();
        break;
      case 'isActive':
        aValue = a.isActive ? 1 : 0;
        bValue = b.isActive ? 1 : 0;
        break;
      case 'createdOn':
        aValue = new Date(a.createdOn).getTime();
        bValue = new Date(b.createdOn).getTime();
        break;
      case 'createdModified':
      case 'modifiedOn':
        aValue = new Date(a.modifiedOn).getTime();
        bValue = new Date(b.modifiedOn).getTime();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleDownload = () => {
    const rows = sortedSubscriptions.map(s => ({
      'Created / Modified On': formatDate(s.modifiedOn),
      'Name':                  s.subscriptionName,
      'Brand':                 s.brandName,
      'Type':                  capitalizeFirst(s.edgeType),
      'Recipient(s)':          s.recipientEmail,
      'State / Territory':     s.state || s.territory || '',
      'Frequency':             s.periodicity,
      'Next Delivery':         !s.cronExpression || s.cronExpression.trim() === ''
                                 ? 'N/A'
                                 : formatDateShort(s.nextDelivery),
      'Is Active':             s.isActive ? 'Yes' : 'No',
    }));

    const worksheet  = XLSX.utils.json_to_sheet(rows);
    const workbook   = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Edge Subscriptions');
    XLSX.writeFile(workbook, 'edge-subscriptions.xlsx');
  };

  if (isFetching && subscriptions.length === 0) {
    return (
      <div className="empty-state">
        <FiRefreshCw size={48} className="spin" />
        <Text textStyle="h3" color="text">
          Loading subscriptions...
        </Text>
      </div>
    );
  }

  if (subscriptions.length === 0) {
    return (
      <div className="empty-state">
        <Text textStyle="h3" color="text">
          No subscriptions yet
        </Text>
        <Text textStyle="s1" color="text">
          When you create your first subscription, it will appear here.
        </Text>
      </div>
    );
  }

  return (
    <div className="edge-subscriptions-list">
      {/* Legend (Filters hidden for now) */}
      <div className="manage-subscriptions-legend">
        <Text textStyle="s1" color="text">
          Here you can edit or delete the subscriptions you have set up.
        </Text>
      </div>

      {/* Filters Section (hidden – not removed) */}
      <div className="filters-section filters-section-hidden">
        <div className="filters-label">
          <Text textStyle="h3" color="text">Filters</Text>
        </div>
        <div className="filters-row">
          <div className="filter-field">
            <input
              type="text"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              placeholder="Email Address:"
              className="filter-input"
            />
          </div>
          <div className="filter-field">
            <input
              type="text"
              value={filterBrandName}
              onChange={(e) => setFilterBrandName(e.target.value)}
              placeholder="Brand Name:"
              className="filter-input"
            />
          </div>
          <div className="filter-field">
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="filter-select"
            >
              {companyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="filter-select"
            >
              {stateOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-field">
            <select
              value={edgeTypeFilter}
              onChange={(e) => setEdgeTypeFilter(e.target.value as 'brand' | 'opportunity' | 'inventory' | 'all')}
              className="filter-select"
            >
              {edgeTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <div className="subscriptions-table">
          <div className="table-header-with-actions">
            <div className="table-header">
              <div className="table-cell header-cell header-cell-two-lines sortable" onClick={() => handleSort('createdModified')}>
                <span className="header-cell-text">
                  <span>CREATED</span>
                  <span>/ MODIFIED ON</span>
                </span>
                {sortField === 'createdModified' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell sortable" onClick={() => handleSort('subscriptionName')}>
                Name
                {sortField === 'subscriptionName' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell sortable" onClick={() => handleSort('brandName')}>
                Brand
                {sortField === 'brandName' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell sortable" onClick={() => handleSort('edgeType')}>
                type
                {sortField === 'edgeType' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell sortable" onClick={() => handleSort('recipientEmail')}>
                Recipient(s)
                {sortField === 'recipientEmail' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell header-cell-two-lines sortable" onClick={() => handleSort('state')}>
                <span className="header-cell-text">
                  <span>STATE</span>
                  <span>/ TERRITORY</span>
                </span>
                {sortField === 'state' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell sortable" onClick={() => handleSort('periodicity')}>
                frequency
                {sortField === 'periodicity' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell sortable" onClick={() => handleSort('nextDelivery')}>
                Next
                {sortField === 'nextDelivery' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell sortable" onClick={() => handleSort('isActive')}>
                Is Active
                {sortField === 'isActive' && (sortDirection === 'asc' ? <FiArrowUp size={14} /> : <FiArrowDown size={14} />)}
              </div>
              <div className="table-cell header-cell">Actions</div>
            </div>
          </div>
          
          <div className="table-body">
            {sortedSubscriptions.map((subscription) => (
              <div 
                key={subscription.id} 
                className="table-row"
              >
                <div className="table-cell created-modified-cell">
                  {formatDate(subscription.modifiedOn)}
                </div>
                <div className="table-cell">{subscription.subscriptionName}</div>
                <div className="table-cell">{subscription.brandName}</div>
                <div className="table-cell">{capitalizeFirst(subscription.edgeType)}</div>
                <div className="table-cell">
                  <div className="recipient-info">
                    <div className="recipient-avatar">
                      {getInitials(subscription.recipientEmail)}
                    </div>
                    <div className="recipient-list">
                      {(() => {
                        const recipients = getRecipientsList(subscription);
                        const maxVisible = 2;
                        const visible = recipients.slice(0, maxVisible);
                        const remainder = recipients.length - maxVisible;
                        return (
                          <>
                            {visible.map((recipient, index) => (
                              <div key={`${subscription.id}-${recipient}-${index}`} className="recipient-email">
                                {recipient}
                              </div>
                            ))}
                            {remainder > 0 && (
                              <div className="recipient-more">+{remainder}</div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="table-cell state-territory-cell">{subscription.state || subscription.territory || '—'}</div>
                <div className="table-cell">
                  {subscription.periodicity}
                  {(!subscription.cronExpression || subscription.cronExpression.trim() === '') && (
                    <span className="next-na" style={{ display: 'block', fontStyle: 'normal' }}>(once)</span>
                  )}
                </div>
                <div className="table-cell">
                  {!subscription.cronExpression || subscription.cronExpression.trim() === '' ? (
                    <span className="next-na">N/A</span>
                  ) : (
                    <div className="sending-on-info">
                      <div>{formatDateShort(subscription.nextDelivery)}</div>
                      <div className="sending-time">At {new Date(subscription.nextDelivery).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                    </div>
                  )}
                </div>
                <div className="table-cell">
                  {(!subscription.cronExpression || subscription.cronExpression.trim() === '') ? (
                    <span className="next-na">N/A</span>
                  ) : (
                    <button
                      type="button"
                      className={`is-active-toggle ${subscription.isActive ? 'on' : 'off'}`}
                      onClick={() => onToggleActive?.(subscription.id)}
                      title={subscription.isActive ? 'Active (click to turn off)' : 'Inactive (click to turn on)'}
                      aria-pressed={subscription.isActive}
                    >
                      <span className="is-active-toggle-thumb">
                        {subscription.isActive ? (
                          <FiCheck className="is-active-toggle-icon" size={12} />
                        ) : (
                          <FiX className="is-active-toggle-icon" size={12} />
                        )}
                      </span>
                    </button>
                  )}
                </div>
                <div className="table-cell actions-cell">
                  <div className="table-actions">
                    <button 
                      className="table-action-btn" 
                      onClick={() => handleEdit(subscription.id)}
                      title="Edit"
                    >
                      <FiEdit size={16} />
                    </button>
                    <button 
                      className="table-action-btn" 
                      onClick={() => handleDuplicate(subscription.id)}
                      title="Duplicate"
                    >
                      <FiCopy size={16} />
                    </button>
                    <button 
                      className="table-action-btn delete-btn" 
                      onClick={() => handleDelete(subscription.id)}
                      title="Delete"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="table-download-footer">
        <Button className="filter-action-btn" onClick={handleDownload}>
          <FiDownload size={18} />
          <span className="action-btn-label">Download</span>
        </Button>
      </div>
      <ConfirmDialog
        isOpen={!!deleteTargetId}
        title="Delete subscription?"
        message="This action cannot be undone. Are you sure you want to delete this subscription?"
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={() => setDeleteTargetId(null)}
        onConfirm={() => {
          if (deleteTargetId) {
            onDelete?.(deleteTargetId);
          }
          setDeleteTargetId(null);
        }}
      />
    </div>
  );
};

export default EdgeSubscriptionsList;
